<script lang="ts">
  // BJL-CHECK: Badge (used, the analysis-layer count), Button, Filter, ToggleGroup,
  // DropdownBare, Menu. The mode control is a glass pill on the moving map whose menu is a
  // never-empty multi-select with a synthetic "All" row — DropdownBare is a form field with its
  // own chrome and would list "All" as a fourth value; the two floating controls are 40px icon
  // tiles on `tileButtonChrome`, where a Button would bring its own sizing and lose the glass.
  // See artifacts/deviations/MapPage.md.
  import TransitMap from '../components/Map/TransitMap.svelte'
  import VehicleSheet from '../components/Map/VehicleSheet.svelte'
  import StationSheet from '../components/Map/StationSheet.svelte'
  import BusStopSheet from '../components/Map/BusStopSheet.svelte'
  import FerryLandingSheet from '../components/Map/FerryLandingSheet.svelte'
  import RailStationSheet from '../components/Map/RailStationSheet.svelte'
  import NjBusStopSheet from '../components/Map/NjBusStopSheet.svelte'
  import NearbyStationsSheet from '../components/Map/NearbyStationsSheet.svelte'
  import LayersSheet from '../components/Map/LayersSheet.svelte'
  import MapSheet from '../components/Map/MapSheet.svelte'
  import FeedHealthPanel from '../components/Map/FeedHealthPanel.svelte'
  import VehicleWatchHud from '../components/Map/VehicleWatchHud.svelte'
  import RiderPanel from '../components/RiderPanel.svelte'
  import { Badge } from '@byjackli-labs/ui/Badge'
  import { parentStopId } from '@traintopia/domain'
  import type {
    StationInfo,
    BusStopInfo,
    FerryLandingInfo,
    RailStationInfo,
    NjBusStopInfo,
  } from '../components/Map/stationInfo'
  import type { TransferContext } from '../components/Map/timetable'
  import { Colors } from '../ui/colors'
  import { GLASS_BG, tileButtonChrome } from '../ui/theme'
  import { safeTop, safeRight, safeBottomButtons } from '../ui/safeArea'
  import { useGeolocation } from '../hooks/useGeolocation.svelte'
  import { useBackHandler } from '../hooks/useBackHandler.svelte'
  import { useRideTracking } from '../hooks/useRideTracking.svelte'
  import { useRiderStore } from '../store/rider'
  import { useLayersStore } from '../store/layers'
  import {
    useTransitStore,
    showsAllModes,
    TRANSIT_MODES,
    type TransitMode,
    type VehiclePosition,
  } from '../store/transit'
  import { useStopsStore } from '../store/stops'
  import { useVehicleWatchStore } from '../store/vehicleWatch'
  import { storeSelect } from '../lib/store.svelte'
  import { api } from '../services/api'

  interface Props {
    onInteraction?: () => void
    // True while one of App's tab sheets is open. Opening one is "another panel" and drops
    // the nearby list — see the effect below.
    sheetOpen?: boolean
  }

  const { onInteraction, sheetOpen = false }: Props = $props()

  let selectedVehicle = $state<VehiclePosition | null>(null)
  let selectedStation = $state<StationInfo | null>(null)
  let selectedBusStop = $state<BusStopInfo | null>(null)
  let selectedFerryLanding = $state<FerryLandingInfo | null>(null)
  let selectedRailStation = $state<RailStationInfo | null>(null)
  let selectedNjBusStop = $state<NjBusStopInfo | null>(null)
  // The connection the rider arrives on, when the sheet was reached by transferring out of a
  // timetable. Owned here because it belongs to the sheet TARGETING, not either sheet's
  // internals — set and cleared by the same calls that re-target them.
  let transferCtx = $state<TransferContext | null>(null)
  let showNearby = $state(false)
  // Set when the sheet was reached from the nearby-stops list, so its header can offer a
  // back that returns there rather than only closing.
  let fromNearby = $state(false)
  let showLayers = $state(false)
  // Official MTA reference map. A full-screen overlay owning its own z-index, so it lives
  // here rather than in the floating stack.
  let showMap = $state(false)
  // The vehicle-mode dropdown under the pill.
  let showModeMenu = $state(false)

  // `riding` = bound to a vehicle. awaiting_board / transfer still show the RiderPanel but
  // must leave the VehicleSheet reachable — tapping a vehicle is how the next leg is boarded.
  const phase = storeSelect(useRiderStore, (s) => s.phase)
  const isRiding = $derived(phase.value === 'riding')
  const sessionActive = $derived(phase.value !== 'idle')
  const watchedVehicleId = storeSelect(useVehicleWatchStore, (s) => s.watchedVehicleId)

  const transitFilter = storeSelect(useTransitStore, (s) => s.transitFilter)

  // A favorite tapped in the Favorites sheet drops its identity here, since that sheet can't
  // reach these local-state detail sheets. Consume it, clear it so it fires once, and open
  // the sheet as a map tap would — showStation/showBusStop call onInteraction, which closes
  // Favorites over the map.
  const pendingStationOpen = storeSelect(useTransitStore, (s) => s.pendingStationOpen)
  const pendingBusStopOpen = storeSelect(useTransitStore, (s) => s.pendingBusStopOpen)
  $effect(() => {
    const s = pendingStationOpen.value
    if (!s) return
    useTransitStore.getState().clearPendingOpen()
    fromNearby = false
    showStation(s)
  })
  $effect(() => {
    const s = pendingBusStopOpen.value
    if (!s) return
    useTransitStore.getState().clearPendingOpen()
    fromNearby = false
    showBusStop(s)
  })

  // A tab sheet is a fresh navigation intent, so drop the nearby list rather than leaving it
  // stacked behind. Reads only `sheetOpen`, so writing showNearby can't re-trigger this.
  $effect(() => {
    if (sheetOpen) showNearby = false
  })

  // Badged on the layers button, so it's always visible that the map is showing something
  // other than plain route colors.
  const analysisCount = storeSelect(
    useLayersStore,
    (s) => Number(s.delays) + Number(s.feedHealth),
  )

  // Station list backing a timetable transfer's route lookup (TransitMap is its fetch owner).
  const stops = storeSelect(useStopsStore, (s) => s.stops)

  const geo = useGeolocation()
  useRideTracking(() => geo.location)

  // Point the detail sheets at a station / bus stop. Callers own the fromNearby flag: a
  // transfer re-targets the sheet in place and must leave the "back to nearby" trail as it
  // found it. `ctx` is non-null only on a "Transfer Here" tap, so every other entry point
  // clears any filter left from a previous transfer.
  function showStation(s: StationInfo, ctx: TransferContext | null = null) {
    onInteraction?.()
    const transit = useTransitStore.getState()
    selectedVehicle = null
    clearFerryLanding()
    clearRailStation()
    clearNjBusStop()
    selectedBusStop = null
    transit.setSelectedBusStopId(null)
    transferCtx = ctx
    selectedStation = s
    transit.setSelectedStationId(s.id)
  }

  function showBusStop(s: BusStopInfo, ctx: TransferContext | null = null) {
    onInteraction?.()
    const transit = useTransitStore.getState()
    selectedVehicle = null
    clearFerryLanding()
    clearRailStation()
    clearNjBusStop()
    selectedStation = null
    transit.setSelectedStationId(null)
    transferCtx = ctx
    selectedBusStop = s
    transit.setSelectedBusStopId(s.id)
  }

  // A ferry landing: the same targeting as a bus stop, including re-targeting in place on a
  // "Transfer Here" from a boat's timetable (the only way `ctx` is ever non-null).
  function showFerryLanding(l: FerryLandingInfo, ctx: TransferContext | null = null) {
    onInteraction?.()
    const transit = useTransitStore.getState()
    selectedVehicle = null
    selectedStation = null
    transit.setSelectedStationId(null)
    selectedBusStop = null
    transit.setSelectedBusStopId(null)
    clearRailStation()
    clearNjBusStop()
    transferCtx = ctx
    selectedFerryLanding = l
    transit.setSelectedFerryLandingId(l.id)
    fromNearby = false
    showNearby = false
  }

  function clearFerryLanding() {
    if (!selectedFerryLanding) return
    selectedFerryLanding = null
    useTransitStore.getState().setSelectedFerryLandingId(null)
  }

  function closeFerryLanding() {
    clearFerryLanding()
    transferCtx = null
  }

  // A LIRR / Metro-North station: the ferry landing's targeting exactly, including the
  // in-place re-target on a "Transfer Here" from a train's timetable.
  function showRailStation(st: RailStationInfo, ctx: TransferContext | null = null) {
    onInteraction?.()
    const transit = useTransitStore.getState()
    selectedVehicle = null
    selectedStation = null
    transit.setSelectedStationId(null)
    selectedBusStop = null
    transit.setSelectedBusStopId(null)
    clearFerryLanding()
    clearNjBusStop()
    transferCtx = ctx
    selectedRailStation = st
    transit.setSelectedRailStationId(st.id)
    fromNearby = false
    showNearby = false
  }

  function clearRailStation() {
    if (!selectedRailStation) return
    selectedRailStation = null
    useTransitStore.getState().setSelectedRailStationId(null)
  }

  function closeRailStation() {
    clearRailStation()
    transferCtx = null
  }

  // An NJ Transit bus stop: the railroad station's targeting exactly, including the in-place
  // re-target on a "Transfer Here" from a bus's timetable.
  function showNjBusStop(st: NjBusStopInfo, ctx: TransferContext | null = null) {
    onInteraction?.()
    const transit = useTransitStore.getState()
    selectedVehicle = null
    selectedStation = null
    transit.setSelectedStationId(null)
    selectedBusStop = null
    transit.setSelectedBusStopId(null)
    clearFerryLanding()
    clearRailStation()
    transferCtx = ctx
    selectedNjBusStop = st
    transit.setSelectedNjBusStopId(st.id)
    fromNearby = false
    showNearby = false
  }

  function clearNjBusStop() {
    if (!selectedNjBusStop) return
    selectedNjBusStop = null
    useTransitStore.getState().setSelectedNjBusStopId(null)
  }

  function closeNjBusStop() {
    clearNjBusStop()
    transferCtx = null
  }

  // Same flow as onStationPress, plus dismissing the nearby list.
  function openStation(s: StationInfo) {
    showStation(s)
    fromNearby = true
    showNearby = false
  }

  // Same, for a nearby bus stop.
  function openBusStop(s: BusStopInfo) {
    showBusStop(s)
    fromNearby = true
    showNearby = false
  }

  // "Transfer Here" from inside a train's timetable. The feed reports platform ids ("127N")
  // while everything station-keyed uses the parent ("127"). The sheet also needs the route
  // list: the stops store has it for every drawn station, the details endpoint covers the
  // rest.
  async function transferToStation(
    stopId: string,
    stopName: string,
    ctx: TransferContext | null,
  ) {
    const id = parentStopId(stopId)
    const known = stops.value.find((s) => s.id === id)
    if (known) return showStation({ id, name: known.name, routes: known.routes }, ctx)
    try {
      const d = await api.getStationDetails(id)
      showStation({ id: d.id, name: d.name, routes: d.routes }, ctx)
    } catch {
      // Unknown station: still open it. Arrivals come from a separate call, and the "Lines
      // at this station" strip just renders empty.
      showStation({ id, name: stopName, routes: [] }, ctx)
    }
  }

  // Back from a sheet opened via the nearby list: dismiss it and reopen that panel.
  function backToNearby() {
    const transit = useTransitStore.getState()
    selectedStation = null
    transit.setSelectedStationId(null)
    selectedBusStop = null
    transit.setSelectedBusStopId(null)
    fromNearby = false
    transferCtx = null
    showNearby = true
  }

  // Full dismissal, dropping the trail that led here. Shared by the ✕ and Android Back.
  function closeStation() {
    selectedStation = null
    useTransitStore.getState().setSelectedStationId(null)
    fromNearby = false
    transferCtx = null
  }
  function closeBusStop() {
    selectedBusStop = null
    useTransitStore.getState().setSelectedBusStopId(null)
    fromNearby = false
    transferCtx = null
  }

  // Android Back, one overlay at a time. The stack is LIFO by open order, so these are just
  // "what Back means while I'm showing". A sheet reached from the nearby list backs out to
  // it, exactly like its ‹ button.
  useBackHandler(() => showModeMenu, () => { showModeMenu = false })
  useBackHandler(() => showMap, () => { showMap = false })
  useBackHandler(() => showLayers, () => { showLayers = false })
  useBackHandler(() => showNearby, () => { showNearby = false })
  useBackHandler(() => !!selectedVehicle && !isRiding, () => { selectedVehicle = null })
  useBackHandler(() => !!selectedStation, () => (fromNearby ? backToNearby() : closeStation()))
  useBackHandler(() => !!selectedBusStop, () => (fromNearby ? backToNearby() : closeBusStop()))
  useBackHandler(() => !!selectedFerryLanding, closeFerryLanding)
  useBackHandler(() => !!selectedRailStation, closeRailStation)
  useBackHandler(() => !!selectedNjBusStop, closeNjBusStop)
  useBackHandler(() => !!watchedVehicleId.value, () => {
    useVehicleWatchStore.getState().stopWatching()
  })

  // Hide the floating right-edge buttons while any detail sheet is up so they don't overlap.
  const anySheetOpen = $derived(
    !!selectedVehicle ||
      !!selectedStation ||
      !!selectedBusStop ||
      !!selectedFerryLanding ||
      !!selectedRailStation ||
      !!selectedNjBusStop ||
      showNearby ||
      showLayers ||
      showMap ||
      !!watchedVehicleId.value,
  )

  const MODE_LABEL: Record<TransitMode, string> = {
    subway: 'Subway',
    bus: 'Bus',
    ferry: 'Ferry',
    rail: 'Rail',
    njbus: 'NJ Bus',
  }
  // The pill is one fixed width whatever it says, so it doesn't jump about the top of the map
  // as modes are ticked. It carries only initials ("S+B"), so the longest label is four of
  // them ("S+B+F+R" — all five is "All"), which is what sets the width now: the MENU under it
  // takes the same width so the two read as one control, and its longest row ("Subway"
  // beside its checkbox) fits with room to spare. NJ Bus's initial is its N.
  const MODE_PILL_W = 120

  const allModes = $derived(showsAllModes(transitFilter.value))
  const modeLabel = $derived(
    allModes ? 'All' : transitFilter.value.map((m) => MODE_LABEL[m][0]).join('+'),
  )
  // Initials are for the eye; a screen reader gets the names.
  const modeSpoken = $derived(
    allModes ? 'All' : transitFilter.value.map((m) => MODE_LABEL[m]).join(' and '),
  )

  // Picking a mode is a "show me the whole subway / whole bus system" ask, so it drops any
  // Routes-panel selection narrowing the map. An active trip plan's focus (tripRouteIds) is
  // left intact, so a change mid-journey doesn't blow away the route the rider is following.
  // Route lines still obey their own layer toggle. The menu stays open: it is a multi-select.
  function pickModes(modes: readonly TransitMode[] | TransitMode) {
    const transit = useTransitStore.getState()
    transit.clearSelectedRoutes()
    if (typeof modes === 'string') transit.toggleTransitMode(modes)
    else transit.setTransitFilter(modes)
  }

  // The tab bar's content height in App.svelte: 6px top padding + a 46px tab. Its BOTTOM
  // padding is `safeBottomButtons`, so the floating right-edge buttons anchor off that same
  // variable plus this — the one offset landing them just above the bar under both gesture
  // and 3-button navigation. Anchoring off `safeBottom` (the full nav inset) leaves them a
  // finger's width too high on a gesture phone. Keep in sync with App.svelte.
  const TAB_BAR_H = 52
  const BTN_GAP = 8   // breathing room between the bar and the stack
  const BTN_W = 40    // the resting tile: as wide as it likes, nothing beside it
  const BTN_STEP = 26 // one resting tile (24px tall) plus 2px between them

  // ---- The ride-session stack's metrics, which are MAPLIBRE'S, not ours ----
  // For a ride session `floatSlot` moves the stack to the top-right corner, and MapLibre's
  // NavigationControl already lives there. Joining a stack means wearing its measurements: a
  // 40px tile 2px out of alignment under a 29px control reads as something that fell off the
  // column rather than the bottom of it. So for the session — and only for it — the tiles take
  // the library's own figures, read out of `maplibre-gl.css`:
  //   `.maplibregl-ctrl-group button` is 29 × 29, `box-sizing: border-box`
  //   `.maplibregl-ctrl-top-right .maplibregl-ctrl` takes `margin: 10px 10px 0 0`
  // Only the CHROME stays ours (`tileButtonChrome`): the corner reads as one column of four
  // squares, of which the top three are MapLibre's white group and the bottom two are the
  // app's dark glass. Matching its paint as well would make our buttons look like zoom
  // controls, which they are not.
  const MAP_TILE = 29
  const MAP_TILE_STEP = MAP_TILE + 2
  /** One figure, because MapLibre's margin is the same 10 on the top and the right. */
  const MAP_CTRL_MARGIN = 10
  /**
   * What the corner costs before our stack can start: MapLibre's own top margin plus the three
   * buttons of a default NavigationControl (zoom in, zoom out, compass) — 97px.
   *
   * The corner container's `--safe-top` padding is added in `index.css`, which is why this is a
   * bare pixel figure stacked onto `safeTop()` rather than a second inset. Without it the pin
   * and Map Options land ON the zoom/compass stack, and ours (z-index 100) paints over a
   * control sitting at MapLibre's z-index 2 — so the zoom buttons stop being tappable for the
   * whole session.
   */
  const MAP_CTRL_BLOCK = MAP_CTRL_MARGIN + 3 * MAP_TILE

  /**
   * Where one button of the floating right-edge stack sits, how big it is, and which EDGE the
   * stack is pinned to — all three change for the duration of a ride session.
   *
   * The stack rests on the tab bar, and the rider drawer now rises from that same bar
   * (`Rider/shell.ts`), so through a whole session both buttons would sit inside its bottom
   * ~72px — under an opaque black panel, since the drawer outranks them (150 against 100).
   * Hiding them is what the filter bar above does, and it is the wrong answer twice over here:
   * layer control never depends on the ride, and a control only reachable by collapsing the
   * sign is worse than no control (the filter bar's own comment says so). So the stack moves
   * to the TOP-right for the session instead. That strip is free the moment the drawer leaves
   * it: the filter bar is already hidden for the whole session, FeedHealthPanel takes the top
   * LEFT, and the watch HUD's banner only appears when these two are hidden anyway.
   *
   * `fromTop` is the slot counting DOWN from the top edge, `fromBar` the slot counting UP from
   * the tab bar — the same visual order (📍 over Map Options) read from whichever edge is
   * currently carrying it. Map Options passes a `fromTop` that depends on the pin, because
   * with location denied the pin is absent and Map Options takes the near slot on both edges.
   *
   * The top edge is not empty, though: MapLibre's own zoom/compass control lives in that
   * corner, so the stack hangs BELOW it (`MAP_CTRL_BLOCK`) rather than at the edge itself —
   * the same `BTN_GAP` it rests on the tab bar with, measured off the other neighbour — and
   * wears that control's width, height and right offset while it is there (see MAP_TILE). The
   * size therefore changes when a ride starts, which is the smaller of the two surprises: the
   * stack is already changing EDGE at the same moment.
   *
   * ⚠️ Only the session branch pays `safeRight`, and that is deliberate rather than an
   * oversight in the other one. Alignment with MapLibre's control is the whole point of this
   * branch and `.maplibregl-ctrl-top-right` pays that inset in `index.css`, so a flat 10px
   * would go ragged again the moment a landscape cutout is under it. The resting stack has no
   * neighbour to line up with and keeps its own 12.
   */
  function floatSlot(fromTop: number, fromBar: number): string {
    return sessionActive
      ? `top: ${safeTop(MAP_CTRL_BLOCK + BTN_GAP + fromTop * MAP_TILE_STEP)};` +
        ` right: ${safeRight(MAP_CTRL_MARGIN)};` +
        ` width: ${MAP_TILE}px; height: ${MAP_TILE}px;`
      : `bottom: ${safeBottomButtons(TAB_BAR_H + BTN_GAP + fromBar * BTN_STEP)};` +
        ` right: 12px; width: ${BTN_W}px; padding: 3px 0;`
  }
</script>

<!-- One row of the mode menu. A ticked row is a solid plate in the MTA's own blue rather
     than tinted text alone: this sits on the moving map, where a colour change by itself is
     not a reliable signal against whatever happens to be underneath it. `locked` is the row
     that cannot be pressed — "All" once everything is shown, or the last mode still ticked. -->
{#snippet modeRow(label: string, checked: boolean, locked: boolean, onpick: () => void)}
  <button
    role="menuitemcheckbox"
    aria-checked={checked}
    aria-disabled={locked}
    onclick={() => { if (!locked) onpick() }}
    style="
      display: flex; align-items: center; gap: 10px;
      padding: 8px 10px; border: none; border-radius: 10px;
      background: none; cursor: {locked ? 'default' : 'pointer'};
      color: {checked ? Colors.text : Colors.textSecondary};
      font-weight: 700; font-size: var(--bjl-font-size-xs); text-align: left;
    "
  >
    <span
      aria-hidden="true"
      style="
        flex: none; box-sizing: border-box; width: 16px; height: 16px; border-radius: 4px;
        display: flex; align-items: center; justify-content: center;
        background: {checked ? '#0039A6' : 'none'};
        border: 1.5px solid {checked ? '#0039A6' : Colors.borderStrong};
      "
    >
      {#if checked}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff"
          stroke-width="4" stroke-linecap="round" stroke-linejoin="round"
        ><path d="M5 12l5 5 9-10" /></svg>
      {/if}
    </span>
    {label}
  </button>
{/snippet}

<div style="position: relative; width: 100%; height: 100%; background-color: {Colors.ground};">
  <!-- Map fills the container -->
  <div style="position: absolute; inset: 0;">
    <TransitMap
      userLocation={geo.location}
      onVehiclePress={(v) => {
        // While riding, the vehicle sheet stays suppressed — the RiderPanel owns vehicle
        // context — but station selection below is still allowed.
        if (isRiding) return
        onInteraction?.()
        const transit = useTransitStore.getState()
        selectedStation = null
        transit.setSelectedStationId(null)
        selectedBusStop = null
        transit.setSelectedBusStopId(null)
        clearFerryLanding()
        clearRailStation()
        clearNjBusStop()
        selectedVehicle = v
        showNearby = false
      }}
      onStationPress={(s) => {
        // Selectable even while riding: the RiderPanel hides for as long as the station
        // sheet is open and is restored from the rider store on close.
        onInteraction?.()
        const transit = useTransitStore.getState()
        selectedVehicle = null
        selectedBusStop = null
        transit.setSelectedBusStopId(null)
        clearFerryLanding()
        clearRailStation()
        clearNjBusStop()
        selectedStation = s
        transit.setSelectedStationId(s.id)
        fromNearby = false
        showNearby = false
      }}
      onBusStopPress={(s) => {
        // Selectable even while riding, same as stations.
        onInteraction?.()
        const transit = useTransitStore.getState()
        selectedVehicle = null
        selectedStation = null
        transit.setSelectedStationId(null)
        clearFerryLanding()
        clearRailStation()
        clearNjBusStop()
        selectedBusStop = s
        transit.setSelectedBusStopId(s.id)
        fromNearby = false
        showNearby = false
      }}
      onFerryLandingPress={(l) => showFerryLanding(l)}
      onRailStationPress={(st) => showRailStation(st)}
      onNjBusStopPress={(st) => showNjBusStop(st)}
    />
  </div>

  <!-- Upstream feed freshness. Renders only when the Feed health layer is on. -->
  <FeedHealthPanel />

  <!-- Filter bar. The inner pill carries the glass styling and clips its contents
       (overflow: hidden).
       z-index sits above the deck.gl overlay canvas (overlaid mode) so passing 3D models
       don't paint over the button; below the detail sheets (VehicleSheet 1000).
       Hidden for the whole ride session, as it is while watching a vehicle. It was hidden
       because the rider drawer covered this strip even at SNAP_MINI; the drawer rises from the
       BOTTOM now, so what keeps it hidden is the second half of that reasoning on its own —
       riding is when a mode switch has least to offer, since the rider is following one line
       rather than browsing. The strip it left is what `floatSlot` moves the pin and Map
       Options onto for the session, so the two controls that DO still apply mid-ride are the
       ones up here; those are laid out from the right edge and this pill is centred. -->
  {#if !watchedVehicleId.value && !sessionActive}
    <!-- Tap-away catcher: closes the menu on the first tap outside it rather than letting
         that tap also land on the map (and open a sheet the rider didn't aim at). -->
    {#if showModeMenu}
      <div
        role="presentation"
        onclick={() => { showModeMenu = false }}
        style="position: absolute; inset: 0; z-index: 49;"
      ></div>
    {/if}
    <div
      style="
        position: absolute; top: {safeTop(16)}; left: 50%; transform: translateX(-50%);
        z-index: 50; width: {MODE_PILL_W}px;
      "
    >
      <button
        onclick={() => { showModeMenu = !showModeMenu }}
        aria-haspopup="menu"
        aria-expanded={showModeMenu}
        aria-label="Vehicles shown: {modeSpoken}"
        style="
          box-sizing: border-box; width: 100%;
          display: grid; grid-template-columns: 12px 1fr 12px; align-items: center; gap: 6px;
          padding: 9px 12px;
          background-color: {GLASS_BG}; backdrop-filter: blur(8px);
          border: 1px solid {showModeMenu ? Colors.primary : Colors.borderStrong};
          border-radius: 999px; cursor: pointer;
          color: {Colors.text}; font-weight: 700; font-size: var(--bjl-font-size-xs);
        "
      >
        <!-- An empty first column as wide as the chevron's, so the label centres on the pill
             itself rather than on the space left of the chevron. -->
        <span aria-hidden="true"></span>
        <span style="text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
          {modeLabel}
        </span>
        <svg
          width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
          fill="none" stroke="currentColor" stroke-width="3"
          stroke-linecap="round" stroke-linejoin="round"
          style="flex: none; transition: transform 150ms; transform: rotate({showModeMenu ? 180 : 0}deg);"
        ><path d="M6 9l6 6 6-6" /></svg>
      </button>

      {#if showModeMenu}
        <!-- A checklist, not a radio list: any mix of modes can be shown at once. "All" is
             every box ticked rather than a value of its own, and the last ticked box can't be
             cleared — a map showing no vehicles at all reads as broken, not as a choice. -->
        <div
          role="menu"
          tabindex="-1"
          aria-label="Vehicles shown"
          onkeydown={(e) => { if (e.key === 'Escape') showModeMenu = false }}
          style="
            margin-top: 6px; padding: 4px;
            display: flex; flex-direction: column; gap: 2px;
            background-color: {GLASS_BG}; backdrop-filter: blur(8px);
            border: 1px solid {Colors.borderStrong}; border-radius: 14px;
          "
        >
          {@render modeRow('All', allModes, allModes, () => pickModes(TRANSIT_MODES))}
          <div style="height: 1px; margin: 2px 6px; background: {Colors.border};"></div>
          {#each TRANSIT_MODES as mode (mode)}
            {@const checked = transitFilter.value.includes(mode)}
            {@render modeRow(
              MODE_LABEL[mode],
              checked,
              checked && transitFilter.value.length === 1,
              () => pickModes(mode),
            )}
          {/each}
        </div>
      {/if}
    </div>
  {/if}

  <!-- Location button: opens the nearby-stations list. Shown only once location permission
       is granted (so we never advertise a feature the user disabled) and hidden while a
       detail/nearby sheet is open. Top of the two-button floating stack, which rides
       directly on the tab bar (see TAB_BAR_H) — or under MapLibre's own zoom control at the
       top edge for the duration of a ride session, see `floatSlot`; everything else that used
       to stack below now lives inside the
       Map Options sheet. When permission is denied this button is absent and Map Options
       keeps the same spot on whichever edge the stack is on. -->
  {#if geo.permissionGranted && !anySheetOpen}
    <button
      onclick={() => {
        onInteraction?.()
        showNearby = true
      }}
      aria-label="Nearby stations"
      style="
        position: fixed; {floatSlot(0, 1)}
        z-index: 100;
        box-sizing: border-box;
        display: flex; align-items: center; justify-content: center;
        {tileButtonChrome}
        border-radius: 4px; cursor: pointer; line-height: 1;
        font-size: 16px;
      "
    >📍</button>
  {/if}

  <!-- Map Options button: the stack's near slot — resting BTN_GAP above the tab bar, or
       directly under the pin when a ride session has moved the stack to the top edge
       (`floatSlot`). Unlike the pin it is always available, since layer control never depends
       on a permission — so when location is denied and the pin is hidden, this is what sits on
       the edge. The badge counts active analysis layers. -->
  {#if !anySheetOpen}
    <button
      onclick={() => {
        onInteraction?.()
        showLayers = true
      }}
      aria-label="Map layers"
      style="
        position: fixed; {floatSlot(geo.permissionGranted ? 1 : 0, 0)}
        z-index: 100;
        box-sizing: border-box;
        display: flex; align-items: center; justify-content: center;
        {tileButtonChrome}
        border: 1px solid {analysisCount.value > 0 ? Colors.primary : Colors.borderStrong};
        color: {analysisCount.value > 0 ? Colors.primary : Colors.textSecondary};
        border-radius: 4px; cursor: pointer; line-height: 1;
        font-size: 16px;
      "
    >
      <!-- Gear, drawn rather than typed: the button's color is meaningful (it goes primary
           when analysis layers are on) and an emoji gear would ignore it — U+2699 falls back
           to Noto Color Emoji in Android's WebView. currentColor keeps the tint working. -->
      <svg
        width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"
        fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round"
        style="display: block;"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
      <!-- The count hangs off a corner, and which corner depends on where the tile is. On the
           resting stack that is the top-RIGHT, over the map. For a ride session the tile is
           MapLibre's 10px from the screen edge instead of 12, and the badge overhangs by 7 —
           leaving 3px of it against the bezel, where a rounded display or a landscape cutout
           clips it. It goes to the top-LEFT there, into the map it already sits over. -->
      {#if analysisCount.value > 0}
        <span
          style="
            position: absolute; top: -7px;
            {sessionActive ? 'left: -7px;' : 'right: -7px;'}
          "
        >
          <Badge size="xs" variant="solid" tone="primary" shape="pill" register="mono">
            {analysisCount.value}
          </Badge>
        </span>
      {/if}
    </button>
  {/if}

  <!-- Map Options: display settings, layer control, and the official-map link. -->
  {#if showLayers}
    <LayersSheet
      onClose={() => { showLayers = false }}
      onOpenOfficialMap={() => {
        showLayers = false
        showMap = true
      }}
      locationGranted={geo.permissionGranted}
      onRequestLocation={geo.requestLocation}
    />
  {/if}

  <!-- Official MTA reference map, full-screen. Opened from the Map Options sheet. -->
  {#if showMap}
    <MapSheet onClose={() => { showMap = false }} />
  {/if}

  <!-- Persistent third-person camera. This is independent of rider state: it keeps
       the selected vehicle in view but never enables onboard-only crowding reports. -->
  <VehicleWatchHud />

  <!-- Nearby stations list: subway stations closest to the user. -->
  {#if showNearby}
    <NearbyStationsSheet
      userLocation={geo.location}
      onSelectStation={openStation}
      onSelectBusStop={openBusStop}
      onClose={() => { showNearby = false }}
    />
  {/if}

  <!-- Rider panel: shown for the whole ride session (waiting to board, riding, or
       mid-transfer). It is a drawer up from the BOTTOM (see `Rider/shell.ts`), which is the
       edge everything else here hangs off too — so it is DELIBERATELY the lowest of them
       (z-index 150 against a detail sheet's 1000), and an open sheet covers it until dismissed.
       That is the cost of the bottom anchor, priced in `panelShell`: the drawer under the
       rider's thumb, in exchange for the next stop being hidden while they read a sheet.
       Ordering here is therefore cosmetic, not the layering — that is z-index's job. -->
  <!-- The `!watchedVehicleId || isRiding` guard is unchanged: watch mode is only reachable
       from the riding face's own 3D toggle, and this keeps the toggle on screen to reverse it. -->
  {#if sessionActive && (!watchedVehicleId.value || isRiding)}
    <RiderPanel />
  {/if}

  <!-- Vehicle details sheet -->
  {#if selectedVehicle && !isRiding}
    <VehicleSheet
      vehicle={selectedVehicle}
      onClose={() => { selectedVehicle = null }}
      onOpenFerryLanding={showFerryLanding}
      onOpenRailStation={showRailStation}
      onOpenNjBusStop={showNjBusStop}
    />
  {/if}

  <!-- Station details sheet: selectable even while riding. -->
  {#if selectedStation}
    <StationSheet
      station={selectedStation}
      onClose={closeStation}
      onBack={fromNearby ? backToNearby : undefined}
      onTransfer={transferToStation}
      transfer={transferCtx}
    />
  {/if}

  <!-- Bus-stop details sheet: lines serving the stop + live arrivals. -->
  {#if selectedBusStop}
    <BusStopSheet
      stop={selectedBusStop}
      onClose={closeBusStop}
      onBack={fromNearby ? backToNearby : undefined}
      onTransfer={showBusStop}
      transfer={transferCtx}
    />
  {/if}

  <!-- Ferry landing: departures by route and direction → one boat's timetable. -->
  {#if selectedFerryLanding}
    <FerryLandingSheet
      landing={selectedFerryLanding}
      onClose={closeFerryLanding}
      onTransfer={showFerryLanding}
      transfer={transferCtx}
    />
  {/if}

  <!-- LIRR / Metro-North station: the departure board → one train's run. -->
  {#if selectedRailStation}
    <RailStationSheet
      station={selectedRailStation}
      onClose={closeRailStation}
      onTransfer={showRailStation}
      transfer={transferCtx}
    />
  {/if}

  <!-- NJ Transit bus stop: the departure board → one bus's run. -->
  {#if selectedNjBusStop}
    <NjBusStopSheet
      stop={selectedNjBusStop}
      onClose={closeNjBusStop}
      onTransfer={showNjBusStop}
      transfer={transferCtx}
    />
  {/if}
</div>
